import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import noop from 'lodash/noop';
import refPropType from 'utils/propTypes/ref';

import style from '../Input.module.scss';
import autoGrowStyles from './AutoGrowTextArea.module.scss';

/**
 * Renders a textarea that will expand/contract in height to accomodate the user's input.
 * Once a user resizes the textarea, the element will remain at its user-set size until unmounted.
 */
export default function AutoGrowTextArea({
  className,
  inputRef,
  onChange: onChangeProp,
  ...otherProps
}) {
  const textareaRef = useRef(null);
  const initialTextAreaHeight = useRef();
  const isDraggingRef = useRef(false);

  const [textAreaDummyContent, setTextAreaDummyContent] = useState(null);
  const [userHasResizedTextArea, setUserHasResizedTextArea] = useState(false);

  // Set dummy content to match input's current value when ready.
  useEffect(() => {
    setTextAreaDummyContent(textareaRef?.current?.value);
  }, [textareaRef.current]);

  // Call the onChange property and update dummy content on change.
  const handleOnChange = event => {
    onChangeProp(event);
    setTextAreaDummyContent(event.target.value);
  };

  // If the user drags inside the textarea to select the input text and the mouseup event
  // happens outside of the textarea, `isDraggingRef.current` will remain `true`, so let's
  // add a listener to the whole `document` when `handleMouseDown` is fired so that we can
  // update `isDraggingRef` correctly if the `mouseup` happens outside the textarea
  const handleGlobalMouseUp = () => {
    isDraggingRef.current = false;

    // Remove the global mouseup listener after mouseup.
    document.removeEventListener('mouseup', handleGlobalMouseUp);
  };

  const handleMouseDown = () => {
    // Store the textarea's initial dimensions when the mouse button is pressed and
    // set `isDragging` to `true` until a mouseup event is detected.
    initialTextAreaHeight.current = textareaRef.current.offsetHeight;
    isDraggingRef.current = true;

    // Add a listener to the document to detect `mouseup` outside of the textarea
    document.addEventListener('mouseup', handleGlobalMouseUp);
  };

  const handleMouseMove = () => {
    // When the user moves their mouse, if `isDragging` is true, check to see whether
    // the height of the textarea has changed - if so, set userHasResizedTextArea to true.
    if (
      isDraggingRef.current &&
      initialTextAreaHeight.current !== textareaRef.current.offsetHeight
    ) {
      setUserHasResizedTextArea(true);
    }
  };

  const handleMouseUp = () => {
    // Compare the initial dimensions with the final dimensions on mouseup.
    isDraggingRef.current = false;

    // Remove event listeners if the user resizes the textarea manually.
    if (userHasResizedTextArea) {
      textareaRef.current.removeEventListener('mousedown', handleMouseDown);
      textareaRef.current.removeEventListener('mousemove', handleMouseMove);
      textareaRef.current.removeEventListener('mouseup', handleMouseUp);
    }
  };

  useEffect(() => {
    // Add 'mousedown', 'mousemove' and 'mouseup' event listeners to the textarea element.
    textareaRef.current.addEventListener('mousedown', handleMouseDown);
    textareaRef.current.addEventListener('mousemove', handleMouseMove);
    textareaRef.current.addEventListener('mouseup', handleMouseUp);

    return () => {
      // Clean up the event listeners if they're still attached on unmount.
      textareaRef.current?.removeEventListener('mousedown', handleMouseDown);
      textareaRef.current?.removeEventListener('mousemove', handleMouseMove);
      textareaRef.current?.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className={autoGrowStyles.growWrap}>
      <textarea
        className={classnames(style.input, autoGrowStyles.input, className)}
        rows="1"
        type="textarea"
        onChange={handleOnChange}
        ref={e => {
          textareaRef.current = e;
          inputRef(e);
        }}
        {...otherProps}
      />

      {
        // Copying the input value to this div element will allow a textarea
        // to grow vertically rather than scroll. Removed from DOM when user
        // resizes the textarea.
        !userHasResizedTextArea && (
          <div
            className={classnames(
              autoGrowStyles.dummyContent,
              autoGrowStyles.input,
              className,
            )}
            aria-hidden
          >
            &nbsp;{textAreaDummyContent}&nbsp;
          </div>
        )
      }
    </div>
  );
}

AutoGrowTextArea.displayName = 'AutoGrowTextArea';
AutoGrowTextArea.propTypes = {
  /**
   * class name to pass into input
   */
  className: PropTypes.string,
  /**
   * reference to underlying `input` dom element
   */
  inputRef: refPropType,
  /**
   * The `onChange` function received from parent
   */
  onChange: PropTypes.func,
};

AutoGrowTextArea.defaultProps = {
  className: '',
  inputRef: null,
  onChange: noop,
};
